import type { User } from "@supabase/supabase-js";

import { AuthMachineStatus, USER_METADATA_KEYS } from "../../constants";

export interface AuthMachineState {
  status: AuthMachineStatus;
  /** Bumped on every status transition (never on context updates). Stamps in-flight operations. */
  generation: number;
  user: User | null;
  /** passwordResetPending only: the server-side password_reset_required=true write hasn't succeeded yet. */
  passwordResetFlagWritePending: boolean;
  isFreshSignIn: boolean;
}

export const initialAuthMachineState: AuthMachineState = {
  status: AuthMachineStatus.BOOTING,
  generation: 0,
  user: null,
  passwordResetFlagWritePending: false,
  isFreshSignIn: false,
};

/**
 * Reducer-level events are already-resolved facts. The store wiring (store.ts)
 * translates raw Supabase events / operation outcomes into these — e.g. boot-time
 * expiry refresh happens in wiring, so BOOT_COMPLETED always carries a live session's user.
 * Events carrying `generation` are results of machine-started operations; the reducer
 * drops them when the stamp doesn't match the current generation (stale result).
 */
export type AuthMachineEvent =
  | { type: "BOOT_COMPLETED"; user: User | null }
  | { type: "BOOT_SESSION_EXPIRED" }
  | { type: "SIGNED_IN"; user: User; freshSignIn: boolean }
  | { type: "RECOVERY_SIGNED_IN"; user: User }
  | { type: "EMAIL_CONFIRMED"; user: User }
  | { type: "USER_REFRESHED"; user: User }
  | { type: "PASSWORD_RESET_ENTERED" }
  | { type: "PASSWORD_UPDATED"; user: User; generation: number }
  | { type: "PASSWORD_UPDATE_FAILED"; generation: number }
  | { type: "PASSWORD_RESET_FLAG_WRITE_SUCCEEDED"; user: User; generation: number }
  | { type: "PASSWORD_RESET_FLAG_WRITE_FAILED"; generation: number }
  | { type: "SIGN_IN_MARKED_COMPLETE" }
  | { type: "SIGNED_OUT"; deliberate: boolean }
  | { type: "SESSION_DIED" };

export const PASSWORD_RESET_REQUIRED_KEY = USER_METADATA_KEYS.PASSWORD_RESET_REQUIRED;

export const isPasswordResetRequired = (user: User): boolean =>
  user.user_metadata?.[PASSWORD_RESET_REQUIRED_KEY] === true;
